import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import Badge, { BadgeVariants } from '@/components/molecules/Badge';
import Breadcrumb from '@/components/molecules/Breadcrumb';
import { ArticleCard } from '@/components/organisms/ArticleCard';
import { Category } from '@/services/wordpress/categories';
import { BlogPreview } from '@/types/blog';
import { useRouter } from 'next/router';
import { useInfiniteArticles } from '../../../hooks/queries/articles';

export interface ArticlesByCategoryTemplateProps {
  mainCategory: Omit<Category, 'count'>;
  categories: Omit<Category, 'count'>[];
  articles: BlogPreview[];
  maxPages: number;
}

export const ArticlesByCategoryTemplate: React.FC<
  ArticlesByCategoryTemplateProps
> = ({ mainCategory, categories, articles, maxPages }) => {
  const router = useRouter();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteArticles({
      perPage: 5,
      exclude: [String(mainCategory.id)],
      queryKey: ['blogPostsByCategory', mainCategory.id],
      initialData: {
        pages: [
          { blogPosts: articles, nextPage: maxPages > 2 ? 2 : undefined },
        ],
        pageParams: [1],
      },
      maxPages,
      initialPageParam: 1,
      enabled: false,
    });

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-col gap-3xl bg-gradient-to-r from-lightTheme-start to-lightTheme-end px-xl pb-3xl pt-xl">
        <div className="py-m">
          <Breadcrumb
            links={[
              {
                text: 'Home',
                href: '/',
              },
              {
                text: 'News',
                href: '/news',
              },
              {
                text: mainCategory.name,
                href: `/news/category/${mainCategory.slug}`,
              },
            ]}
          />
        </div>
        <div>
          <h2 className="text-h2SemiBold">{mainCategory.name}</h2>
        </div>
      </div>
      <div className="flex flex-col bg-neutral-100 md:flex-row md:gap-xl">
        <div className="flex flex-col gap-l bg-neutral-100 px-l pb-3xl pt-xl md:w-2/3">
          <div className="flex flex-col gap-xl">
            {data?.pages?.map((page) =>
              page.blogPosts.map((article) => (
                <ArticleCard layout="row" key={article.id} article={article} />
              ))
            )}
            {hasNextPage && (
              <Button
                variant={ButtonVariants.Secondary}
                aria-label={`Fetch more articles for ${mainCategory.name}`}
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
                disabled={isFetchingNextPage}
              >
                Show more
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-l bg-white px-xl py-l md:flex-1 md:bg-neutral-100">
          <div>
            <h3 className="text-h3SemiBold">Categories</h3>
          </div>
          <div className="flex flex-wrap gap-l">
            {categories.map((category) => (
              <Badge
                variant={BadgeVariants.Blue}
                key={category.id}
                className="mb-m"
                label={category.name}
                onClick={() => router.push(`/news/category/${category.slug}`)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
